// Viewer geo (country/region/city) as detected by CloudFront and attached to /tracking/session payloads.

export interface ViewerGeo {
    country?: string;
    region?: string;
    city?: string;
}

const GEO_KEY = 'vv_geo';
const GEO_ENDPOINT = '/api/geo';
const GEO_TIMEOUT_MS = 2000;

let pending: Promise<ViewerGeo> | null = null;

function compact(data: { country?: string | null; region?: string | null; city?: string | null }): ViewerGeo {
    const geo: ViewerGeo = {};
    if (data.country) geo.country = data.country;
    if (data.region) geo.region = data.region;
    if (data.city) geo.city = data.city;
    return geo;
}

/** Geo already resolved this session, or {} — never triggers a request. */
export function getCachedViewerGeo(): ViewerGeo {
    if (typeof window === 'undefined') return {};
    try {
        const raw = sessionStorage.getItem(GEO_KEY);
        return raw ? compact(JSON.parse(raw)) : {};
    } catch {
        return {};
    }
}

/** Resolves the viewer geo; resolves {} (never rejects) when unavailable. */
export function getViewerGeo(): Promise<ViewerGeo> {
    if (typeof window === 'undefined') return Promise.resolve({});

    try {
        const cached = getCachedViewerGeo();
        if (cached.country && cached.region && cached.city) return Promise.resolve(cached);
    } catch { /* storage unavailable — fall through to fetch */ }

    if (!pending) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), GEO_TIMEOUT_MS);
        pending = fetch(GEO_ENDPOINT, { cache: 'no-store', signal: controller.signal })
            .then((res) => (res.ok ? res.json() : {}))
            .then((data) => {
                const geo = compact(data || {});
                if (geo.country) {
                    try { sessionStorage.setItem(GEO_KEY, JSON.stringify(geo)); } catch { /* ignore */ }
                }
                return geo;
            })
            .catch(() => ({}))
            .finally(() => {
                clearTimeout(timer);
                pending = null;
            });
    }
    return pending;
}
